import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const MAX_PM_WINDOWS = 3;

/**
 * Custom hook for PM window management
 */
export const usePMWindows = (state, user) => {
  const {
    openPMWindows, setOpenPMWindows,
    pmQueue, setPmQueue,
    setUnreadPmCount, setUnreadBySender
  } = state;

  const fetchUnreadPmCount = useCallback(async () => {
    if (user?.role === 'guest') {
      setUnreadPmCount(0);
      setUnreadBySender([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/pm/unread-count`);
      setUnreadPmCount(response.data.total_unread);
      setUnreadBySender(response.data.unread_by_sender || []);
    } catch (error) {
      console.error('Failed to fetch unread PM count:', error);
    }
  }, [user?.role, setUnreadPmCount, setUnreadBySender]);

  const openPMWindow = useCallback(async (friend) => {
    if (openPMWindows.find(w => w.friend.id === friend.id)) {
      toast.info('PM window already open');
      return;
    }

    try {
      await axios.put(`${API}/pm/${friend.id}/mark-read`);
      fetchUnreadPmCount();
    } catch (error) {
      console.error('Failed to mark PMs as read:', error);
    }

    const isMobile = window.innerWidth < 768;
    if (isMobile && openPMWindows.length > 0) {
      setOpenPMWindows([]);
    }

    if (!isMobile && openPMWindows.length >= MAX_PM_WINDOWS) {
      const oldest = openPMWindows[0];
      setOpenPMWindows(prev => prev.slice(1));
      setPmQueue(prev => [...prev, oldest.friend]);
      toast.info(`Closed PM with ${oldest.friend.username} (max ${MAX_PM_WINDOWS} windows)`);
    }

    setOpenPMWindows(prev => [...prev, { friend, isMinimized: false, isMaximized: isMobile }]);
  }, [openPMWindows, setOpenPMWindows, setPmQueue, fetchUnreadPmCount]);

  const closePMWindow = useCallback((friendId) => {
    setOpenPMWindows(prev => prev.filter(w => w.friend.id !== friendId));
    
    if (pmQueue.length > 0) {
      const nextFriend = pmQueue[0];
      setPmQueue(prev => prev.slice(1));
      setOpenPMWindows(prev => [...prev, { friend: nextFriend, isMinimized: false, isMaximized: false }]);
      toast.info(`Reopened PM with ${nextFriend.username}`);
    }
  }, [pmQueue, setOpenPMWindows, setPmQueue]);

  const toggleMinimizePMWindow = useCallback((friendId) => {
    setOpenPMWindows(prev => prev.map(w => 
      w.friend.id === friendId ? { ...w, isMinimized: !w.isMinimized } : w
    ));
  }, [setOpenPMWindows]);

  const toggleMaximizePMWindow = useCallback((friendId) => {
    setOpenPMWindows(prev => prev.map(w => 
      w.friend.id === friendId ? { ...w, isMaximized: !w.isMaximized } : w
    ));
  }, [setOpenPMWindows]);

  return {
    openPMWindow,
    closePMWindow,
    toggleMinimizePMWindow,
    toggleMaximizePMWindow,
    fetchUnreadPmCount
  };
};

export default usePMWindows;
